import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "../../../../components/ui/form";
import { Button } from "../../../../components/ui/button";
import { SheetContent, SheetTitle } from "../../../../components/ui/sheet";
import { useToast } from "../../../../hooks/use-toast";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { useEffect, useState, useCallback } from "react";
import Spinner from "../../../../components/spinner.components";
import { Plus, X } from "lucide-react";
import { useSearchVendorsHttp } from "../../../../hooks/http/admin/vendors/search-vendors.admin.http";
import { FormSearch } from "../../../../components/form-components/form-api-search.components";
import { debounce, handleHttpError } from "../../../../utils/utils";
import type { IVendor } from "../../../../types/models/vendor.modals.types";
import CheckIcon from "../../../../assets/svgs/common/check.svg";
import { useCreateVendorDraftAdminHttp } from "../../../../hooks/http/admin/posts/create-vendor-draft.admin.http";
import { useParams } from "react-router-dom";
import { useAddSocialAdminHttp } from "../../../../hooks/http/admin/socials/add-social.http";
import { SocialPlatforms } from "../../../../types/models/social.models.types";
import { useGetProfileDetailsAdminHttp } from "../../../../hooks/http/admin/campaigns/utils/get-profile-details.admin.http";
import type { IInstagramUserProfile } from "../../../../types/models/instagram-user-profile.models.types";
import type { ISocial } from "../../../../types/models/social.models.types";
import { ExclamationIcon } from "../../../../assets/svgs/common/exlamation.svg";
import { InstagramIcon } from "../../../../assets/svgs/common/instagram.svg";
import millify from "millify";

const VendorDraftSchema = z.object({
	instagramHandle: z.string().min(1, { message: "Enter Instagram handle" }),
	vendorId: z.string().min(1, { message: "Select a vendor" }),
	payoutAmount: z.coerce.number().min(1, {
		message: "Enter a valid payout amount",
	}),
});

const FormSchema = z.object({
	vendorDrafts: z
		.array(VendorDraftSchema)
		.min(1, { message: "Add at least one vendor draft" }),
});

type VendorDraftData = z.infer<typeof VendorDraftSchema>;
type FormData = z.infer<typeof FormSchema>;

// Profile data structure
interface ProfileData {
	instagramUserProfile: IInstagramUserProfile;
	social: ISocial | null;
}

export default function AddVendorDraftAdminPartial({
	reset,
}: {
	reset: boolean;
}) {
	const { toast } = useToast();
	const [vendors, setVendors] = useState<IVendor[]>();

	// States for tracking progress
	const [draftSuccess, setDraftSuccess] = useState<Record<number, boolean>>({});
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Profile checking states - properly typed
	const [profileDetails, setProfileDetails] = useState<
		Record<number, ProfileData | null>
	>({});
	const [profileChecked, setProfileChecked] = useState<Record<number, boolean>>(
		{},
	);

	const { campaignId } = useParams();

	// HTTP hooks with built-in loading states
	const { execute: addSocialHttp, fetching: addingSocial } =
		useAddSocialAdminHttp();
	const {
		execute: searchVendorsHttp,
		fetching: searchingVendors,
		cancel: cancelSearchVendors,
	} = useSearchVendorsHttp();

	const {
		execute: createVendorDraftHttp,
		fetching: creatingVendorDraft,
		cancel: cancelCreateVendorDraft,
	} = useCreateVendorDraftAdminHttp();

	const {
		execute: getProfileDetailsHttp,
		fetching: fetchingProfileDetails,
		cancel: cancelGetProfileDetails,
	} = useGetProfileDetailsAdminHttp();

	// Debounced profile details fetch
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const debouncedGetProfileDetails = useCallback(
		debounce(async (handle: string, index: number) => {
			cancelGetProfileDetails();
			if (!handle.trim() || !campaignId) return;

			setProfileChecked((prev) => ({ ...prev, [index]: false }));

			try {
				const response = await getProfileDetailsHttp({
					body: null,
					pathParams: { campaignId },
					queryParams: { handle },
				});

				if (!response.success) {
					handleHttpError(response, toast);
					setProfileDetails((prev) => ({ ...prev, [index]: null }));
					return;
				}

				// Store structured profile data
				const profileData: ProfileData = {
					instagramUserProfile: response.data.instagramUserProfile,
					social: response.data.social || null,
				};

				setProfileDetails((prev) => ({ ...prev, [index]: profileData }));
				setProfileChecked((prev) => ({ ...prev, [index]: true }));
			} catch (error) {
				console.error("Error fetching profile details:", error);
				setProfileDetails((prev) => ({ ...prev, [index]: null }));
				setProfileChecked((prev) => ({ ...prev, [index]: true }));
			}
		}, 500),
		[getProfileDetailsHttp, campaignId, toast],
	);

	const form = useForm<FormData>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			vendorDrafts: [
				{
					instagramHandle: "",
					vendorId: "",
					payoutAmount: 0,
				},
			],
		},
	});

	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: "vendorDrafts",
	});

	// Watch for handle changes and fetch profile details
	useEffect(() => {
		const subscription = form.watch((value, { name }) => {
			if (
				name?.startsWith("vendorDrafts.") &&
				name.endsWith(".instagramHandle")
			) {
				const index = Number.parseInt(name.split(".")[1]);
				const handle = value.vendorDrafts?.[index]?.instagramHandle;

				if (handle?.trim()) {
					// Use debounced function
					debouncedGetProfileDetails(handle.trim(), index);
				} else {
					// Clear all profile data if handle is empty
					setProfileDetails((prev) => {
						const newDetails = { ...prev };
						delete newDetails[index];
						return newDetails;
					});
					setProfileChecked((prev) => {
						const newObj = { ...prev };
						delete newObj[index];
						return newObj;
					});
				}
			}
		});

		return () => subscription.unsubscribe();
	}, [form, debouncedGetProfileDetails]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const searchVendors = useCallback(
		debounce(async (searchTerm: string) => {
			cancelSearchVendors();

			if (searchTerm.trim().length === 0) {
				cancelSearchVendors();
				setVendors([]);
				return;
			}

			try {
				const response = await searchVendorsHttp({
					body: null,
					queryParams: searchTerm.trim()
						? { searchString: searchTerm }
						: undefined,
				});

				if (!response.success) {
					return handleHttpError(response, toast);
				}

				setVendors(response.data.vendors);
			} catch (error) {
				console.error("Error searching vendors:", error);
				if (!isSubmitting) {
					toast({
						content: "Failed to search vendors",
						toastType: "error",
					});
				}
			}
		}, 300),
		[searchVendorsHttp, toast, cancelSearchVendors, isSubmitting],
	);

	async function addSocial(handle: string) {
		const response = await addSocialHttp({
			body: {
				handle,
				platform: SocialPlatforms.instagram,
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		return true;
	}

	// Create individual vendor draft function
	async function createVendorDraft(data: VendorDraftData, index: number) {
		if (!campaignId) return false;
		try {
			const profileData = profileDetails[index];

			// If social not on Solo, add it first
			if (profileData && !profileData.social) {
				const socialAdded = await addSocial(data.instagramHandle);
				if (!socialAdded) return false;
			}

			console.log(`Creating vendor draft ${index + 1}:`, data);
			const response = await createVendorDraftHttp({
				body: {
					handle: data.instagramHandle,
					userId: data.vendorId,
					fixedPayoutAmount: data.payoutAmount,
					campaignId: campaignId,
				},
			});

			if (!response.success) {
				handleHttpError(response, toast);
				return false;
			}
			console.log(`Vendor draft ${index + 1} created successfully.`);

			return true;
		} catch (error) {
			console.error(`Error creating vendor draft ${index + 1}:`, error);
			return false;
		}
	}

	// Updated onSubmit with for loop and sequential processing
	async function onSubmit(data: FormData) {
		setIsSubmitting(true);
		setDraftSuccess({}); // Reset success tracking

		try {
			for (let i = 0; i < data.vendorDrafts.length; i++) {
				const success = await createVendorDraft(data.vendorDrafts[i], i);

				if (success) {
					// Mark this draft as successful
					setDraftSuccess((prev) => ({ ...prev, [i]: true }));
				} else {
					// Stop the process on first failure
					setIsSubmitting(false);
					return;
				}
			}

			// All drafts successful - cleanup
			document.getElementById("closeSheet")?.click();
			form.reset();
			setDraftSuccess({});
			setVendors([]);
			setProfileDetails({});
			setProfileChecked({});
		} catch (error) {
			console.error("Error in onSubmit:", error);
		} finally {
			setIsSubmitting(false);
		}
	}

	const addVendorDraft = () => {
		if (fields.length < 10) {
			append({
				instagramHandle: "",
				vendorId: "",
				payoutAmount: 0,
			});
		}
	};

	const removeVendorDraft = (index: number) => {
		if (fields.length > 1) {
			remove(index);
			// Clean up all tracking for removed index
			setDraftSuccess((prev) => {
				const newSuccess = { ...prev };
				delete newSuccess[index];
				return newSuccess;
			});
			setProfileDetails((prev) => {
				const newDetails = { ...prev };
				delete newDetails[index];
				return newDetails;
			});
			setProfileChecked((prev) => {
				const newObj = { ...prev };
				delete newObj[index];
				return newObj;
			});
		}
	};

	// Show form validation errors as toast (only when not submitting)
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (form.formState.errors && !isSubmitting) {
			const errors = form.formState.errors;
			if (errors.vendorDrafts && Array.isArray(errors.vendorDrafts)) {
				const firstErrorIndex = errors.vendorDrafts.findIndex((error) => error);
				if (firstErrorIndex !== -1) {
					const firstError = errors.vendorDrafts[firstErrorIndex];
					const errorKey = Object.keys(firstError || {})[0];
					if (errorKey && firstError) {
						toast({
							content:
								fields.length > 1
									? `Draft ${firstErrorIndex + 1}: ${firstError[errorKey as keyof typeof firstError]?.message}`
									: firstError[errorKey as keyof typeof firstError]?.message,
							toastType: "error",
						});
					}
				}
			}
		}
	}, [form.formState.errors]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (reset) {
			form.reset({
				vendorDrafts: [
					{
						instagramHandle: "",
						vendorId: "",
						payoutAmount: 0,
					},
				],
			});
			setVendors([]);
			setDraftSuccess({});
			setProfileDetails({});
			setProfileChecked({});
			cancelCreateVendorDraft();
			cancelSearchVendors();
		}
	}, [reset]);

	return (
		<SheetContent className="flex flex-col p-0 border-border border-l w-full max-w-2xl">
			<SheetTitle className="p-5 border-b border-border font-semibold text-2xl text-white">
				Add vendor draft
			</SheetTitle>

			<div className="flex flex-col flex-1 gap-6 overflow-y-auto">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="flex flex-col gap-6 h-full"
					>
						<div className="flex flex-col flex-1 gap-8 p-5">
							{fields.map((field, index) => {
								const isDraftSuccessful = draftSuccess[index];
								const profileData = profileDetails[index];
								const isProfileChecked = profileChecked[index];
								const isFetchingDetails = fetchingProfileDetails;
								const showFields =
									isProfileChecked && !isFetchingDetails && profileData;
								const socialNotOnSolo = profileData && !profileData.social;

								return (
									<div
										key={field.id}
										className={`relative flex flex-col gap-6 p-4 border rounded-lg transition-all duration-300 ${
											isDraftSuccessful
												? "border-green-500 bg-green-500/10"
												: "border-border"
										}`}
									>
										{fields.length > 1 && !isDraftSuccessful && (
											<Button
												type="button"
												variant="ghost"
												size="sm"
												onClick={() => removeVendorDraft(index)}
												className="top-2 right-2 absolute hover:bg-transparent p-2 text-gray-400 hover:text-gray-400"
												disabled={isSubmitting}
											>
												<X className="w-4 h-4" />
											</Button>
										)}

										{fields.length > 1 && (
											<div className="mb-2">
												<h3 className="flex items-center gap-1.5 font-medium text-white">
													{isDraftSuccessful && (
														<CheckIcon className="w-4 h-4" />
													)}
													Draft {index + 1}
												</h3>
											</div>
										)}

										{/* Instagram Handle */}
										<div className="relative">
											<FormInput
												name={`vendorDrafts.${index}.instagramHandle`}
												label="Enter instagram handle"
												placeholder="Enter handle"
												required
												form={form}
												prefix="@"
												type="text"
												disabled={
													isDraftSuccessful ||
													creatingVendorDraft ||
													isFetchingDetails
												}
											/>
										</div>

										{/* Profile Details Display */}
										{profileData && !isFetchingDetails && (
											<div className="flex items-center gap-1.5 bg-tertiarybuttonbg p-4 border-2 border-border rounded-lg">
												<div className="border border-border rounded-full w-10 h-10" />
												<div className="flex flex-col">
													<p className="text-semibold">
														{profileData.instagramUserProfile.username}
													</p>
													<p className="flex items-center gap-1 text-sm">
														<InstagramIcon />
														{millify(
															profileData.instagramUserProfile.followersCount,
														)}
													</p>
												</div>
											</div>
										)}

										{/* Show vendor and payout fields only after profile details are checked */}
										{showFields && (
											<>
												{/* Select Vendor */}
												<FormSearch
													form={form}
													name={`vendorDrafts.${index}.vendorId`}
													label="Select vendor"
													placeholder="Search and select vendor"
													required
													options={
														vendors?.map((vendor) => ({
															value: vendor.id,
															label: vendor.name,
															data: vendor,
														})) || []
													}
													onSearch={searchVendors}
													fetching={searchingVendors}
													showAllOnEmpty={true}
													maxResultsWhileSearching={5}
													emptyMessage="No vendors found"
													disabled={isDraftSuccessful || creatingVendorDraft}
												/>

												{/* Payout Amount */}
												<div className="relative">
													<FormInput
														name={`vendorDrafts.${index}.payoutAmount`}
														label="Enter fixed payout amount"
														placeholder="Enter amount"
														required
														form={form}
														prefix="₹"
														type="number"
														disabled={isDraftSuccessful || creatingVendorDraft}
													/>
												</div>

												{/* Social not on Solo warning */}
												{socialNotOnSolo && (
													<div className="flex items-center">
														<ExclamationIcon />
														<span className="text-sm text-yellow-500">
															Not on solo
														</span>
													</div>
												)}
											</>
										)}

										{/* Loading spinner for current draft being processed */}
										{isSubmitting &&
											!isDraftSuccessful &&
											Object.keys(draftSuccess).length === index && (
												<div className="flex justify-center items-center gap-2 bg-tertiarybuttonbg p-4 border border-border rounded-lg">
													<Spinner width={20} />
													<span className="text-gray-400 text-sm">
														{addingSocial
															? "Adding social..."
															: "Creating draft..."}
													</span>
												</div>
											)}
									</div>
								);
							})}

							{/* Add More Button */}
							{fields.length < 10 && !isSubmitting && (
								<Button
									type="button"
									variant="ghost"
									onClick={addVendorDraft}
									className="flex items-center gap-2 hover:bg-white/5 p-4 border border-border rounded-lg text-gray-400 hover:text-white"
								>
									<Plus className="w-4 h-4" />
									Add more ({fields.length}/10)
								</Button>
							)}
						</div>

						{/* Submit Button */}
						<div className="bottom-0 z-10 sticky bg-background p-5 border-t border-border">
							{fetchingProfileDetails ? (
								<Button
									type="button"
									disabled={fetchingProfileDetails}
									className="gap-2 bg-tertiarybuttonbg w-full h-14 font-semibold text-primarybuttontext text-white tracking-wider"
								>
									<Spinner width={15} /> Fetching details
								</Button>
							) : (
								<Button
									disabled={
										isSubmitting ||
										Object.keys(draftSuccess).length === fields.length
									}
									type="submit"
									className="flex gap-2 bg-primarybuttonbg py-6 w-full font-semibold text-primarybuttontext"
								>
									{isSubmitting ? (
										<div className="flex items-center gap-2">
											<Spinner width={20} />
											Processing drafts... ({Object.keys(draftSuccess).length}/
											{fields.length})
										</div>
									) : Object.keys(draftSuccess).length === fields.length ? (
										"All drafts created successfully ✓"
									) : (
										<>Add vendor draft →</>
									)}
								</Button>
							)}
						</div>
					</form>
				</Form>
			</div>
		</SheetContent>
	);
}
